/**
 * Design Memory — deterministic similarity strategy (categorical).
 *
 * Scores two fingerprints across the specified dimensions and blends them into
 * an overall score. No vectors, no embeddings — pure set/sequence/categorical
 * math. This is the swap point for future semantic or visual embeddings: a
 * replacement strategy with the same `(fpA, fpB, weights) → { overall,
 * dimensions }` signature plugs in without touching any consumer.
 */

import {
  SIMILARITY_DIMENSIONS, DIMENSION_COMPARE, DIMENSION_FIELD, WEIGHTS, VISUAL_DENSITY,
} from './memoryVocabulary';
import { getGenome } from '../genome';

const round2 = (n) => Math.round(n * 100) / 100;

/** Jaccard overlap of two sets (arrays). Both empty ⇒ 0 (no shared evidence). */
export const jaccard = (a = [], b = []) => {
  if (a.length === 0 && b.length === 0) return 0;
  const setB = new Set(b);
  const inter = a.filter((x) => setB.has(x)).length;
  const union = new Set([...a, ...b]).size;
  return union === 0 ? 0 : inter / union;
};

/** Fraction of adjacent ordered pairs of `a` preserved (same relative order) in `b`. */
const orderAgreement = (a = [], b = []) => {
  if (a.length === 0) return 0;
  if (a.length === 1) return b.includes(a[0]) ? 1 : 0;
  let matches = 0;
  let pairs = 0;
  for (let i = 0; i < a.length - 1; i++) {
    pairs += 1;
    const iA = b.indexOf(a[i]);
    const iB = b.indexOf(a[i + 1]);
    if (iA !== -1 && iB !== -1 && iA < iB) matches += 1;
  }
  return pairs === 0 ? 0 : matches / pairs;
};

/** Ordered-sequence similarity: set overlap + order agreement. */
export const sequenceSim = (a = [], b = []) => {
  if (a.length === 0 || b.length === 0) return 0;
  return 0.5 * jaccard(a, b) + 0.5 * orderAgreement(a, b);
};

/** Exact categorical match. */
export const categoricalSim = (a, b) => (a && b && a === b ? 1 : 0);

/** Ordinal similarity along a scale (distance-based); falls back to exact match. */
export const ordinalSim = (a, b, scale) => {
  if (!a || !b) return 0;
  const iA = scale.indexOf(a);
  const iB = scale.indexOf(b);
  if (iA === -1 || iB === -1) return a === b ? 1 : 0;
  return 1 - Math.abs(iA - iB) / (scale.length - 1);
};

/** Genome similarity: same genome ⇒ 1, same family ⇒ 0.5, else 0. */
export const genomeSim = (a, b) => {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const fa = getGenome(a);
  const fb = getGenome(b);
  return fa && fb && fa.family === fb.family ? 0.5 : 0;
};

const compareDimension = (dim, fpA, fpB) => {
  const field = DIMENSION_FIELD[dim];
  const a = fpA[field];
  const b = fpB[field];
  switch (DIMENSION_COMPARE[dim]) {
    case 'tokens': return jaccard(a, b);
    case 'set': return jaccard(a, b);
    case 'sequence': return sequenceSim(a, b);
    case 'categorical': return categoricalSim(a, b);
    case 'ordinal': return ordinalSim(a, b, VISUAL_DENSITY);
    case 'genome': return genomeSim(a, b);
    default: return 0;
  }
};

/**
 * Compute the similarity of two fingerprints.
 * @param {Object} fpA
 * @param {Object} fpB
 * @param {Object} [weights]
 * @returns {{ overall: number, dimensions: Object<string, number> }}
 */
export const computeSimilarity = (fpA, fpB, weights = WEIGHTS) => {
  const dimensions = {};
  let overall = 0;
  SIMILARITY_DIMENSIONS.forEach((dim) => {
    const s = round2(compareDimension(dim, fpA, fpB));
    dimensions[dim] = s;
    overall += (weights[dim] || 0) * s;
  });
  return { overall: round2(overall), dimensions };
};
